import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const RELATIONSHIPS = ["spouse", "partner", "child", "parent", "friend", "other"];
const TRAVEL_STYLES = ["luxury", "adventure", "romantic", "family", "business", "wellness"];
const PACES = ["leisurely", "balanced", "fast"];
const HOTEL_PREFS = ["boutique", "resort", "villa", "urban"];
const SEAT_PREFS = ["window", "aisle", "first class", "business"];

export default function TravelPreferencesSection({ user }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [familyDraft, setFamilyDraft] = useState({ name: "", relationship: "spouse", age: "", dietary: "" });

  useEffect(() => {
    if (!user?.membership_tier) {
      setLoading(false);
      return;
    }
    api
      .get("/me/preferences")
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs({}))
      .finally(() => setLoading(false));
  }, [user?.membership_tier]);

  if (!user?.membership_tier) return null;
  if (loading) return null;

  const update = (patch) => setPrefs((p) => ({ ...(p || {}), ...patch }));

  const updateList = (key, value) => {
    const list = (prefs?.[key] || []).map((s) => s.trim()).filter(Boolean);
    update({ [key]: value.split(",").map((s) => s.trim()).filter(Boolean) });
  };

  const addFamily = () => {
    if (!familyDraft.name.trim()) return;
    const list = [...(prefs.family_members || []), {
      ...familyDraft,
      age: familyDraft.age ? parseInt(familyDraft.age) : null,
    }];
    update({ family_members: list });
    setFamilyDraft({ name: "", relationship: "spouse", age: "", dietary: "" });
  };

  const removeFamily = (idx) => {
    update({ family_members: prefs.family_members.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/me/preferences", prefs);
      setPrefs(r.data);
      toast.success("Travel preferences saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-24 pt-16 border-t border-[var(--emerald)]/10" data-testid="travel-prefs-section">
      <div className="text-center max-w-3xl mx-auto">
        <div className="overline">{user.membership_tier} member</div>
        <h2 className="font-display text-5xl text-[var(--emerald)] mt-4">Your Travel Preferences</h2>
        <p className="mt-4 text-[var(--ink-2)]">
          The details that let Sasha and your concierge anticipate what you love. Update anytime.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mt-12">
        {/* Travel style */}
        <div className="lux-card">
          <div className="overline">Travel style</div>
          <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">How you like to travel</h3>

          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            <div>
              <div className="overline mb-1">Style</div>
              <select
                className="lux-input"
                value={prefs?.travel_style || ""}
                onChange={(e) => update({ travel_style: e.target.value })}
                data-testid="prefs-travel-style"
              >
                <option value="">Choose…</option>
                {TRAVEL_STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div className="overline mb-1">Pace</div>
              <select
                className="lux-input"
                value={prefs?.pace || ""}
                onChange={(e) => update({ pace: e.target.value })}
                data-testid="prefs-pace"
              >
                <option value="">Choose…</option>
                {PACES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div className="overline mb-1">Hotel preference</div>
              <select
                className="lux-input"
                value={prefs?.hotel_preference || ""}
                onChange={(e) => update({ hotel_preference: e.target.value })}
                data-testid="prefs-hotel"
              >
                <option value="">Choose…</option>
                {HOTEL_PREFS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div className="overline mb-1">Seat preference</div>
              <select
                className="lux-input"
                value={prefs?.seat_preference || ""}
                onChange={(e) => update({ seat_preference: e.target.value })}
                data-testid="prefs-seat"
              >
                <option value="">Choose…</option>
                {SEAT_PREFS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="overline mb-1">Budget range</div>
              <input
                className="lux-input"
                placeholder="$5,000 – $15,000 per trip"
                value={prefs?.budget_range || ""}
                onChange={(e) => update({ budget_range: e.target.value })}
                data-testid="prefs-budget"
              />
            </div>
          </div>
        </div>

        {/* Destinations & dietary */}
        <div className="lux-card">
          <div className="overline">Where & what</div>
          <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">Destinations & dietary</h3>

          <div className="space-y-5 mt-6">
            <div>
              <div className="overline mb-1">Favorite destinations (comma-separated)</div>
              <input
                className="lux-input"
                placeholder="Amalfi Coast, Kyoto, Cape Town"
                value={(prefs?.favorite_destinations || []).join(", ")}
                onChange={(e) => updateList("favorite_destinations", e.target.value)}
                data-testid="prefs-favorite"
              />
            </div>
            <div>
              <div className="overline mb-1">Bucket list</div>
              <input
                className="lux-input"
                placeholder="Patagonia, Northern Lights, Maldives"
                value={(prefs?.bucket_list || []).join(", ")}
                onChange={(e) => updateList("bucket_list", e.target.value)}
                data-testid="prefs-bucket"
              />
            </div>
            <div>
              <div className="overline mb-1">Dietary</div>
              <input
                className="lux-input"
                placeholder="Pescatarian, no shellfish"
                value={prefs?.dietary || ""}
                onChange={(e) => update({ dietary: e.target.value })}
                data-testid="prefs-dietary"
              />
            </div>
            <div>
              <div className="overline mb-1">Allergies</div>
              <input
                className="lux-input"
                placeholder="Penicillin, tree nuts"
                value={prefs?.allergies || ""}
                onChange={(e) => update({ allergies: e.target.value })}
                data-testid="prefs-allergies"
              />
            </div>
          </div>
        </div>

        {/* Family info */}
        <div className="lux-card lg:col-span-2">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="overline">Family & travel companions</div>
              <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">Who you travel with</h3>
              <p className="text-sm text-[var(--ink-2)] mt-2 max-w-xl">
                Names, relationships, and dietary needs help your concierge prepare every journey.
              </p>
            </div>
          </div>

          {/* List */}
          {(prefs?.family_members || []).length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              {prefs.family_members.map((f, i) => (
                <div key={f.id || `${f.name || "fm"}-${f.relationship || ""}-${i}`} className="border border-[var(--gold)]/30 p-4 flex items-start gap-3" data-testid={`family-member-${i}`}>
                  <div className="flex-1">
                    <div className="font-display text-2xl text-[var(--emerald)]">{f.name}</div>
                    <div className="overline mt-0.5">{f.relationship}{f.age ? ` · ${f.age}` : ""}</div>
                    {f.dietary && <div className="text-sm text-[var(--ink-2)] mt-1">{f.dietary}</div>}
                  </div>
                  <button
                    onClick={() => removeFamily(i)}
                    className="text-[var(--brand-red)] hover:opacity-70"
                    data-testid={`family-remove-${i}`}
                    aria-label="Remove"
                  >
                    <Trash2 size={14} strokeWidth={1.25} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add row */}
          <div className="grid sm:grid-cols-5 gap-3 mt-6 items-end">
            <div className="sm:col-span-2">
              <div className="overline mb-1">Name</div>
              <input
                className="lux-input"
                value={familyDraft.name}
                onChange={(e) => setFamilyDraft({ ...familyDraft, name: e.target.value })}
                data-testid="family-name"
                placeholder="e.g. Olivia"
              />
            </div>
            <div>
              <div className="overline mb-1">Relationship</div>
              <select
                className="lux-input"
                value={familyDraft.relationship}
                onChange={(e) => setFamilyDraft({ ...familyDraft, relationship: e.target.value })}
                data-testid="family-relationship"
              >
                {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <div className="overline mb-1">Age</div>
              <input
                className="lux-input"
                type="number"
                value={familyDraft.age}
                onChange={(e) => setFamilyDraft({ ...familyDraft, age: e.target.value })}
                data-testid="family-age"
              />
            </div>
            <div>
              <button
                onClick={addFamily}
                className="btn-gold w-full"
                disabled={!familyDraft.name.trim()}
                data-testid="family-add-btn"
              >
                <Plus size={14} strokeWidth={1.5} /> Add
              </button>
            </div>
            <div className="sm:col-span-5">
              <div className="overline mb-1">Dietary / notes for this person (optional)</div>
              <input
                className="lux-input"
                value={familyDraft.dietary}
                onChange={(e) => setFamilyDraft({ ...familyDraft, dietary: e.target.value })}
                placeholder="Gluten-free; loves spa days"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="lux-card lg:col-span-2">
          <div className="overline">Anything else</div>
          <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">Notes for your concierge</h3>
          <textarea
            className="lux-input min-h-[100px] mt-4"
            placeholder="Honeymoon coming up · Always traveling with a small dog · Anniversary in October…"
            value={prefs?.notes || ""}
            onChange={(e) => update({ notes: e.target.value })}
            data-testid="prefs-notes"
          />
        </div>
      </div>

      <div className="flex justify-center mt-10">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="prefs-save-btn"
        >
          <Save size={14} strokeWidth={1.5} /> {saving ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </section>
  );
}
